import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { HARDCODED_ACTOR_UUID } from "../api/helpers";
import { useCreateProfileQA } from "../api/ProfilesApi";
import { ReportSummaryItemApi, SourceTypeFilter } from "../api/Reports";
import { usePermissionValidator } from "../api/UserPermissionValidators";
import { useQaDone } from "../api/WorkflowApi";
import Button, { ButtonColorTypes } from "../shared-components/Button";
import ErrorMessageContainer from "../shared-components/ErrorMessageContainer";
import AuditQAProfiles from "./AuditQAProfiles";
import AuditQAPosts from "./AuditQAPosts";
import { FlagCountResponse, GroupCount } from "../api/Items";
import {
  WORKFLOW_STEP_STATUS_ENUM,
  WorkflowRunRowDataResponse,
} from "../api/Workflows";
import { useConfirmationDialog } from "../shared-components/ConfirmationDialog";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import { useFamaFlags } from "../api/FamaFlags";
import { classNames } from "../utils";

const ProfileQA = ({
  handleExit,
  report,
  updateFlagCount,
  updateKeywordCount,
  sourceTypeFilter,
  postTypeFilter,
  dateFilter,
  behaviorFilter,
  keywordFilter,
  topicFilter,
  itemTypeFilter,
  updateQAStep,
  subStep,
  workflowRun,
  isLoadingWorkflowRun,
  errorWorkflowRun,
}: {
  handleExit: () => void;
  report: ReportSummaryItemApi;
  sourceTypeFilter: SourceTypeFilter;
  postTypeFilter: { original: boolean; shared: boolean; liked: boolean };
  dateFilter: { fromDate: string; toDate: string };
  behaviorFilter: string[];
  keywordFilter: string[];
  topicFilter: string[];
  itemTypeFilter: string[];
  updateFlagCount: (flags: FlagCountResponse) => void;
  updateKeywordCount: (keywords: GroupCount) => void;
  updateQAStep: (step: string) => void;
  subStep: string;
  workflowRun: WorkflowRunRowDataResponse | undefined;
  isLoadingWorkflowRun: boolean;
  errorWorkflowRun: boolean;
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const { canInternalQaEdit, canClientQaEdit, canNavAmendment } =
    usePermissionValidator();
  const { enableIqaRowFetch } = useFamaFlags();

  // This route can be used to view a 360 misconduct report even when the report
  // is no longer actively waiting on a QA step.
  const in360MisconductQaRoute =
    location.pathname.includes("/360/") &&
    location.pathname.includes("/misconduct-qa");

  const isInternalQa =
    report.step_name === WORKFLOW_STEP_STATUS_ENUM.InternalQA ||
    workflowRun?.steps_in_progress.includes(
      WORKFLOW_STEP_STATUS_ENUM.InternalQA
    );
  const isClientQa =
    report.step_name === WORKFLOW_STEP_STATUS_ENUM.ClientQA ||
    workflowRun?.steps_in_progress.includes(WORKFLOW_STEP_STATUS_ENUM.ClientQA);
  const isAmendmentQa =
    report.step_name === WORKFLOW_STEP_STATUS_ENUM.AmendmentQA ||
    workflowRun?.steps_in_progress.includes(
      WORKFLOW_STEP_STATUS_ENUM.AmendmentQA
    );
  const has360MisconductQaStep =
    report.step_name === WORKFLOW_STEP_STATUS_ENUM.ThreeSixtyQA ||
    report.step_name === WORKFLOW_STEP_STATUS_ENUM.ThreeSixtyPlusReview ||
    report.step_name === WORKFLOW_STEP_STATUS_ENUM.ThreeSixtyWebSearch ||
    report.step_name === WORKFLOW_STEP_STATUS_ENUM.ThreeSixtySubworkflow ||
    workflowRun?.steps_in_progress.includes(
      WORKFLOW_STEP_STATUS_ENUM.ThreeSixtyQA
    ) ||
    workflowRun?.steps_in_progress.includes(
      WORKFLOW_STEP_STATUS_ENUM.ThreeSixtyPlusReview
    ) ||
    workflowRun?.steps_in_progress.includes(
      WORKFLOW_STEP_STATUS_ENUM.ThreeSixtyWebSearch
    ) ||
    workflowRun?.steps_in_progress.includes(
      WORKFLOW_STEP_STATUS_ENUM.ThreeSixtySubworkflow
    );
    const getStepName = (steps: string[] = []) => {
      if (!steps || steps.length === 0) {
        return WORKFLOW_STEP_STATUS_ENUM.ClientQA;
      }
      return steps.find(
        (step) =>
          step === WORKFLOW_STEP_STATUS_ENUM.InternalQA ||
          step === WORKFLOW_STEP_STATUS_ENUM.AmendmentQA ||
          step === WORKFLOW_STEP_STATUS_ENUM.ClientQA ||
          step === WORKFLOW_STEP_STATUS_ENUM.ThreeSixtyQA ||
          step === WORKFLOW_STEP_STATUS_ENUM.ThreeSixtyPlusReview ||
          step === WORKFLOW_STEP_STATUS_ENUM.ThreeSixtyWebSearch
      );
    };
  
    const getFirstThreeSixtyQaStepFromSteps = (
      steps: string[]
    ): string | undefined =>
      steps.find(
        (step) =>
          step === WORKFLOW_STEP_STATUS_ENUM.ThreeSixtyQA ||
          step === WORKFLOW_STEP_STATUS_ENUM.ThreeSixtyPlusReview ||
          step === WORKFLOW_STEP_STATUS_ENUM.ThreeSixtyWebSearch
      );
  
    /** Resolves the step shown in the header and used for 360-specific nav; misconduct-qa also uses this for qa_done when report.step_name is e.g. CompletePostProcessing. */
    const currentStepName = (() => {
      if (in360MisconductQaRoute) {
        const steps = workflowRun?.steps_in_progress ?? [];
        const fromStep = getFirstThreeSixtyQaStepFromSteps(steps);
        if (fromStep) return fromStep;
        const sn = report.step_name;
        if (
          sn === WORKFLOW_STEP_STATUS_ENUM.ThreeSixtyQA ||
          sn === WORKFLOW_STEP_STATUS_ENUM.ThreeSixtyPlusReview ||
          sn === WORKFLOW_STEP_STATUS_ENUM.ThreeSixtyWebSearch
        ) {
          return sn;
        }
        return WORKFLOW_STEP_STATUS_ENUM.ThreeSixtyQA;
      }
      if (enableIqaRowFetch) {
        return getStepName(workflowRun?.steps_in_progress || []) || "ClientQA";
      }
      return report.step_name ?? "";
    })();  

  const is360MisconductQa =
    has360MisconductQaStep &&
    !isInternalQa &&
    !isClientQa &&
    !isAmendmentQa;
  const canAdvanceStep =
    (canInternalQaEdit && isInternalQa) ||
    (canClientQaEdit && isClientQa) ||
    (canNavAmendment && isAmendmentQa) ||
    (canInternalQaEdit && is360MisconductQa);
  const {
    isLoading: isLoadingQaDone,
    error: errorQaDone,
    mutate: qaDone,
    isSuccess: successQaDone,
  } = useQaDone();
  const { mutateAsync: createProfile, isLoading: isLoadingCreateProfile } =
    useCreateProfileQA();
  const [newProfiles, setNewProfiles] = useState<string[]>([]);
  const { show, modal, hide } = useConfirmationDialog();

  const createNewProfiles = async () => {
    if (!report.report_uuid || !newProfiles) return;
    if (newProfiles.length > 0) {
      const promises = newProfiles.map((newProfile) => {
        return createProfile({
          profile_url: newProfile,
          report_uuid: report.report_uuid,
          actor_uuid: HARDCODED_ACTOR_UUID,
        });
      });
      await Promise.all(promises).then(() => {
        if (
          report.step_name === WORKFLOW_STEP_STATUS_ENUM.AmendmentQA ||
          workflowRun?.steps_in_progress.includes(
            WORKFLOW_STEP_STATUS_ENUM.AmendmentQA
          )
        ) {
          return;
        }
        qaDone({
          reportUUID: report.report_uuid,
          stepName: resolveStepNameForQaDone(),
        });
      });
    }
    return true;
  };

  const stepDone = useCallback(() => {
    const stepName = in360MisconductQaRoute
      ? currentStepName
      : enableIqaRowFetch
      ? getStepName(workflowRun?.steps_in_progress || []) || "ClientQA"
      : report.step_name;

    // Handle 360 routes with dynamic report UUID
    if (stepName === WORKFLOW_STEP_STATUS_ENUM.ThreeSixtyQA) {
      navigate(`/360/${report.report_uuid}/qa-workbench`, {
        state: { stepDone: true },
      });
      return;
    }
    if (stepName === WORKFLOW_STEP_STATUS_ENUM.ThreeSixtyPlusReview) {
      navigate(`/360/${report.report_uuid}/workbench`, {
        state: { stepDone: true },
      });
      return;
    }
    if (stepName === WORKFLOW_STEP_STATUS_ENUM.ThreeSixtyWebSearch) {
      navigate(`/360/${report.report_uuid}/workbench`, {
        state: { stepDone: true },
      });
      return;
    }

    const routeMap = {
      [WORKFLOW_STEP_STATUS_ENUM.InternalQA]: "/reports/internal_qa",
      [WORKFLOW_STEP_STATUS_ENUM.ClientQA]: "/reports/client_qa",
      [WORKFLOW_STEP_STATUS_ENUM.AmendmentQA]: "/reports/amendment",
    } as const;

    const targetRoute = routeMap[stepName as keyof typeof routeMap];

    if (targetRoute) {
      navigate(targetRoute, { state: { stepDone: true } });
    }
  }, [
    navigate,
    report.step_name,
    report.report_uuid,
    enableIqaRowFetch,
    workflowRun?.steps_in_progress,
    in360MisconductQaRoute,
    currentStepName,
  ]);

  useEffect(() => {
    // Don't redirect if workflow run is still loading
    if (isLoadingWorkflowRun || (!workflowRun && !errorWorkflowRun)) {
      return;
    }

    if (
      !in360MisconductQaRoute &&
      !isInternalQa &&
      !isClientQa &&
      !isAmendmentQa &&
      !is360MisconductQa
    ) {
      navigate("/reports", {
        state: { toast: "Report was no longer waiting on QA" },
      });
    }
    if (successQaDone) {
      stepDone();
    }
  }, [
    navigate,
    successQaDone,
    stepDone,
    isInternalQa,
    isClientQa,
    isAmendmentQa,
    is360MisconductQa,
    in360MisconductQaRoute,
    isLoadingWorkflowRun,
    workflowRun,
    errorWorkflowRun,
  ]);

  const is360Step =
    currentStepName === WORKFLOW_STEP_STATUS_ENUM.ThreeSixtyQA ||
    currentStepName === WORKFLOW_STEP_STATUS_ENUM.ThreeSixtyPlusReview ||
    currentStepName === WORKFLOW_STEP_STATUS_ENUM.ThreeSixtyWebSearch;

    const resolveStepNameForQaDone = (): string => {
      if (in360MisconductQaRoute) return currentStepName;
      if (enableIqaRowFetch) {
        return getStepName(workflowRun?.steps_in_progress || []) || "ClientQA";
      }
      return report.step_name || "ClientQA";
    };

  const handleBackToList = () => {
    // Handle 360 routes with dynamic report UUID
    if (currentStepName === WORKFLOW_STEP_STATUS_ENUM.ThreeSixtyQA) {
      navigate(`/360/${report.report_uuid}/qa-workbench`);
      return;
    }
    if (currentStepName === WORKFLOW_STEP_STATUS_ENUM.ThreeSixtyPlusReview) {
      navigate(`/360/${report.report_uuid}/workbench`);
      return;
    }
    if (currentStepName === WORKFLOW_STEP_STATUS_ENUM.ThreeSixtyWebSearch) {
      navigate(`/360/${report.report_uuid}/workbench`);
      return;
    }

    // For all other steps, use the normal handleExit
    handleExit();
  };

  const handleNext = () => {
    if (report.report_uuid) {
      if (subStep === "profiles") {
        createNewProfiles().then(() => {
          if (newProfiles.length === 0 || isAmendmentQa) {
            updateQAStep("posts");
          }
        });
        return;
      }
      if (subStep === "posts") {
        show(
          "Complete Review?",
          "",
          (action) => { },
          <>
            <span className="mt-4">
              By clicking Complete you will complete the report and return to
              the report list
            </span>
            <div className="flex justify-end">
              <Button
                text="Cancel"
                colorType={ButtonColorTypes.cancel}
                className="w-50 mr-4"
                onClick={() => {
                  hide();
                }}
              ></Button>
              <Button
                text="Complete"
                className="w-50"
                onClick={() => {
                  qaDone({
                    reportUUID: report.report_uuid,
                    stepName: resolveStepNameForQaDone(),
                  });
                }}
              ></Button>
            </div>
          </>,
          false
        );
      }
    }
  };
  function stepNameToTitle(stepName?: string | null) {
    if (stepName === "InternalQA") return "Internal Investigator";
    if (stepName === "ClientQA") return "Client Investigator";
    if (stepName === "AmendmentQA") return "Amendment";
    if (
      stepName === WORKFLOW_STEP_STATUS_ENUM.ThreeSixtyQA ||
      stepName === WORKFLOW_STEP_STATUS_ENUM.ThreeSixtyPlusReview ||
      stepName === WORKFLOW_STEP_STATUS_ENUM.ThreeSixtyWebSearch
    ) {
      return subStep === "profiles"
        ? "360 Misconduct Profile Confirm"
        : "360 Misconduct Post Edit";
    }
    return "";
  }

  if (isLoadingQaDone || isLoadingWorkflowRun) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      {modal}
      <div className="flex items-center justify-between mb-6">
        <span
          className={classNames(
            is360Step ? "text-2xl font-bold" : "text-3xl font-bold"
          )}
        >
          {stepNameToTitle(currentStepName)}
        </span>
        <div className="flex items-center">
          {!is360Step && (
            <span
              className="leading-4 text-xs font-medium mr-4 text-orange-500 cursor-pointer"
              onClick={handleBackToList}
            >
              Back to List
            </span>
          )}
          {is360Step && subStep === "posts" && (
            <span
              className=" text-gray-600 inline-block pr-4 cursor-pointer"
              onClick={() => {
                updateQAStep("profiles");
              }}
            >
              Back to Profile Confirm
            </span>
          )}
          {canAdvanceStep && !isLoadingCreateProfile && (
            <Button
              text={
                subStep === "profiles"
                  ? "Complete Profile Review"
                  : "Complete Post Review"
              }
              className="w-50"
              onClick={handleNext}
            />
          )}
        </div>
      </div>
      {errorQaDone && (
        <ErrorMessageContainer message="Error advancing the report" />
      )}
      {subStep === "profiles" && (
        <AuditQAProfiles
          report={report}
          isLoadingCreateProfile={isLoadingCreateProfile}
          setNewProfiles={setNewProfiles}
        />
      )}
      {subStep === "posts" && (
        <AuditQAPosts
          report={report}
          isLoadingQaDone={isLoadingQaDone}
          sourceTypeFilter={sourceTypeFilter}
          postTypeFilter={postTypeFilter}
          dateFilter={dateFilter}
          behaviorFilter={behaviorFilter}
          keywordFilter={keywordFilter}
          topicFilter={topicFilter}
          itemTypeFilter={itemTypeFilter}
          updateFlagCount={updateFlagCount}
          updateKeywordCount={updateKeywordCount}
        />
      )}
    </div>
  );
};

export default ProfileQA;
